import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Smartphone, Tablet, MapPin, LogIn, LogOut } from "lucide-react";
import api from "@/lib/api";

// Parse user-agent secara ringkas (bukan library berat, cukup detect device type).
function parseUA(ua) {
  if (!ua) return { device: "Unknown", browser: "Unknown", Icon: Monitor };
  const s = ua.toLowerCase();
  let device = "Desktop";
  let Icon = Monitor;
  if (/(iphone|ipod)/.test(s)) { device = "iPhone"; Icon = Smartphone; }
  else if (/ipad/.test(s)) { device = "iPad"; Icon = Tablet; }
  else if (/android/.test(s)) { device = /mobile/.test(s) ? "Android" : "Android Tablet"; Icon = /mobile/.test(s) ? Smartphone : Tablet; }
  else if (/mac os x/.test(s)) device = "Mac";
  else if (/windows/.test(s)) device = "Windows";
  else if (/linux/.test(s)) device = "Linux";
  let browser = "Browser";
  if (/edg\//.test(s)) browser = "Edge";
  else if (/chrome\//.test(s) && !/edg\//.test(s)) browser = "Chrome";
  else if (/firefox\//.test(s)) browser = "Firefox";
  else if (/safari\//.test(s) && !/chrome\//.test(s)) browser = "Safari";
  return { device, browser, Icon };
}

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

/**
 * Session History panel — collapsible di ProfileDialog.
 * Menampilkan 50 event terakhir (login/logout) dengan IP + device.
 * Session yang sedang aktif ditandai dengan badge "AKTIF".
 */
export default function SessionHistorySection({ open, targetUserId = null }) {
  const [events, setEvents] = useState([]);
  const [activeSid, setActiveSid] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const url = targetUserId ? `/admin/users/${targetUserId}/session-history` : "/auth/session-history";
    api.get(url)
      .then(({ data }) => {
        setEvents(data.events || []);
        setActiveSid(data.active_session_id || "");
      })
      .catch((e) => {
        // Admin endpoint boleh 403 — silent.
        if (e.response?.status !== 403) toast.error("Gagal memuat riwayat sesi");
      })
      .finally(() => setLoading(false));
  }, [open, targetUserId]);

  if (loading) {
    return <div className="text-[11px] italic text-slate-400 px-3 py-4 text-center">Memuat riwayat sesi…</div>;
  }
  if (events.length === 0) {
    return <div className="text-[11px] italic text-slate-400 px-3 py-4 text-center">Belum ada aktivitas sesi tercatat</div>;
  }

  return (
    <div className="border border-slate-200 max-h-72 overflow-y-auto" data-testid="session-history-list">
      <table className="w-full text-[10px]">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
          <tr>
            <th className="text-left px-2 py-1.5">Waktu</th>
            <th className="text-left px-2 py-1.5">Aktivitas</th>
            <th className="text-left px-2 py-1.5">Device / Browser</th>
            <th className="text-left px-2 py-1.5">IP</th>
            <th className="text-left px-2 py-1.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => {
            const { device, browser, Icon } = parseUA(ev.user_agent);
            const isLogin = ev.action === "login";
            const isActive = isLogin && ev.session_id && ev.session_id === activeSid;
            return (
              <tr key={ev.id} className={`border-t border-slate-100 hover:bg-slate-50 ${isActive ? "bg-emerald-50/40" : ""}`} data-testid={`session-row-${ev.id}`}>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap tabular-nums">{fmtWhen(ev.created_at)}</td>
                <td className="px-2 py-1.5">
                  {isLogin ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold uppercase tracking-wider">
                      <LogIn size={10} /> Login
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-500 font-semibold uppercase tracking-wider">
                      <LogOut size={10} /> Logout
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-slate-700">
                  <div className="inline-flex items-center gap-1">
                    <Icon size={11} className="text-slate-500" />
                    <span>{device}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{browser}</span>
                  </div>
                </td>
                <td className="px-2 py-1.5 text-slate-600 font-mono">
                  <div className="inline-flex items-center gap-1">
                    <MapPin size={10} className="text-slate-400" />
                    {ev.ip || <span className="text-slate-300">—</span>}
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  {isActive ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white" data-testid={`session-active-${ev.id}`}>
                      ● Sesi Aktif
                    </span>
                  ) : (
                    <span className="text-[9px] text-slate-400 uppercase tracking-wider">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-1.5 text-[9px] italic text-slate-400 bg-slate-50 border-t border-slate-200">
        Menampilkan 50 aktivitas terakhir. Bila melihat login yang tidak dikenal, segera ganti password.
      </div>
    </div>
  );
}
